
import { motion, useScroll, useTransform } from "framer-motion";
import { useState, useEffect } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Menu01Icon, XVariableCircleIcon } from "@hugeicons/core-free-icons";
import { ShinyButton } from "../ui/shiny-button";

export function Navbar() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isPastHero, setIsPastHero] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isPageShinyButtonVisible, setIsPageShinyButtonVisible] = useState(false);
    const [isStoppingGlow, setIsStoppingGlow] = useState(false);
    const { scrollY } = useScroll();

    const backgroundColor = useTransform(
        scrollY,
        [0, 50],
        ["rgba(0, 0, 0, 0)", "rgba(0, 0, 0, 0.8)"]
    );

    const backdropBlur = useTransform(
        scrollY,
        [0, 50],
        ["blur(0px)", "blur(12px)"]
    );

    useEffect(() => {
        // Initial check using framer-motion's scrollY
        const initialScroll = scrollY.get();
        setIsScrolled(initialScroll > 50);
        const viewportHeight = window.innerHeight;
        setIsPastHero(initialScroll > viewportHeight + 50);
        
        return scrollY.on("change", (latest) => {
            setIsScrolled(latest > 50);
            
            // Hero section is min-h-screen, so check if we've scrolled past viewport height
            const viewportHeight = window.innerHeight;
            // Add a small buffer to ensure we're definitely past the hero
            setIsPastHero(latest > viewportHeight + 50);
        });
    }, [scrollY]);

    useEffect(() => {
        // Watch for shiny buttons on the page (not in header)
        // Use requestAnimationFrame to ensure DOM is ready
        let observers: IntersectionObserver[] = [];
        let retryTimeout: NodeJS.Timeout | null = null;
        let visibleCount = 0;

        const handleIntersection = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    visibleCount++;
                } else {
                    visibleCount = Math.max(0, visibleCount - 1);
                }
            });
            setIsPageShinyButtonVisible(visibleCount > 0);
        };

        const setupObservers = () => {
            const allShinyButtons = document.querySelectorAll('.shiny-cta');
            const nav = document.querySelector('nav');
            const pageShinyButtons = Array.from(allShinyButtons).filter((button) => {
                return nav && !nav.contains(button);
            });
            
            if (pageShinyButtons.length === 0) {
                // Retry after a short delay if no buttons found yet
                retryTimeout = setTimeout(setupObservers, 100);
                return;
            }

            pageShinyButtons.forEach((button) => {
                const observer = new IntersectionObserver(handleIntersection, {
                    threshold: 0.1, // Button is considered visible when 10% is in viewport
                    rootMargin: '0px',
                });
                observer.observe(button);
                observers.push(observer);
            });
        };

        const rafId = requestAnimationFrame(() => {
            setupObservers();
        });

        return () => {
            cancelAnimationFrame(rafId);
            if (retryTimeout) {
                clearTimeout(retryTimeout);
            }
            observers.forEach((observer) => observer.disconnect());
        };
    }, []);

    // Handle graceful stop when page shiny button becomes visible
    useEffect(() => {
        if (isPageShinyButtonVisible && isPastHero && !isStoppingGlow) {
            // Start stopping sequence
            setIsStoppingGlow(true);
            
            // After transition duration, complete the stop
            const stopTimeout = setTimeout(() => {
                setIsStoppingGlow(false);
            }, 600);
            
            return () => {
                clearTimeout(stopTimeout);
            };
        } else if (!isPageShinyButtonVisible && isStoppingGlow) {
            // If page button disappears while stopping, cancel the stop
            setIsStoppingGlow(false);
        }
    }, [isPageShinyButtonVisible, isPastHero, isStoppingGlow]);

    const shouldGlow = isPastHero && !isPageShinyButtonVisible && !isStoppingGlow;
    const shouldStop = isStoppingGlow && isPastHero;

    return (
        <motion.nav
            style={{ backgroundColor, backdropFilter: backdropBlur }}
            className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between border-b border-transparent transition-colors duration-300"
        >
            <div className="flex items-center gap-8">
                <a href="#" className="text-2xl font-bold tracking-tighter" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                    T<span className="text-gray-400">BOX</span>
                </a>
            </div>

            <div className="flex items-center gap-4">
                <div className="hidden md:block">
                    <ShinyButton 
                        glow={shouldGlow} 
                        stopping={shouldStop} 
                        small={isScrolled}
                        onClick={() => {
                            const contactSection = document.getElementById('contact');
                            if (contactSection) {
                                contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                        }}
                    >
                        Termin Vereinbaren
                    </ShinyButton>
                </div>
                <button 
                    className="md:hidden text-white p-2 -mr-2 touch-manipulation" 
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label="Toggle menu"
                    style={{ touchAction: 'manipulation' }}
                >
                    {isMobileMenuOpen ? <HugeiconsIcon icon={XVariableCircleIcon} size={24} /> : <HugeiconsIcon icon={Menu01Icon} size={24} />}
                </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 right-0 bg-black/95 backdrop-blur-xl p-6 flex flex-col gap-4 border-b border-white/10 md:hidden"
                >
                    <div className="w-full mt-2">
                        <ShinyButton 
                            glow={shouldGlow} 
                            stopping={shouldStop} 
                            small={isScrolled}
                            className="w-full min-h-[44px]" 
                            onClick={() => {
                                setIsMobileMenuOpen(false);
                                setTimeout(() => {
                                    const contactSection = document.getElementById('contact');
                                    if (contactSection) {
                                        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                }, 100);
                            }}
                        >
                            Termin Vereinbaren
                        </ShinyButton>
                    </div>
                </motion.div>
            )}
        </motion.nav>
    );
}
